import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { trackEvent } from '@/lib/analytics';

interface FormData {
  ageRange: [number, number];
  race: string[];
  politics: string;
  feminist: string;
  children: string;
  bodyType: string;
  familyStructure: string;
  maritalStatus: string;
  sexualPartnerCount: string;
  educationalLevel: string;
}

interface CalculatorFormProps {
  onCalculate: (data: FormData) => void;
  /** Prefill from a shared link. */
  initialData?: FormData;
}

const defaultFormData: FormData = {
  ageRange: [22, 35],
  race: ['all'],
  politics: 'no-preference',
  feminist: 'no-preference',
  children: 'no-preference',
  bodyType: 'no-preference',
  familyStructure: 'no-preference',
  maritalStatus: 'no-preference',
  sexualPartnerCount: 'no-preference',
  educationalLevel: 'no-preference'
};

export function CalculatorForm({ onCalculate, initialData }: CalculatorFormProps) {
  const [formData, setFormData] = useState<FormData>(initialData ?? defaultFormData);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    trackEvent('factor_changed', { factor: field, calculator: 'women' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCalculate(formData);
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Partner Compatibility Calculator</CardTitle>
        <CardDescription>
          Enter your preferences to calculate statistical probability based on U.S. demographic data
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Age Range */}
          <div className="space-y-3">
            <Label>Age Range: {formData.ageRange[0]} - {formData.ageRange[1]} years</Label>
            <Slider
              value={formData.ageRange}
              onValueChange={(value) => updateField('ageRange', value as [number, number])}
              min={18}
              max={100}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau American Community Survey data
            </p>
          </div>

          {/* Race/Ethnicity */}
          <div className="space-y-3">
            <Label>Race/Ethnicity Preference (Select all that apply)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: 'all', label: 'All (No Preference)' },
                { value: 'white', label: 'White (Non-Hispanic)' },
                { value: 'black', label: 'Black or African American' },
                { value: 'hispanic', label: 'Hispanic or Latino' },
                { value: 'asian', label: 'Asian' },
                { value: 'native-american', label: 'Native American or Alaska Native' },
                { value: 'middle-eastern', label: 'Middle Eastern or North African' },
                { value: 'pacific-islander', label: 'Pacific Islander' },
                { value: 'multiracial', label: 'Multiracial/Mixed Race' },
                { value: 'other', label: 'Other' }
              ].map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={option.value}
                    checked={formData.race.includes(option.value)}
                    onCheckedChange={(checked) => {
                      if (option.value === 'all') {
                        // If "All" is selected, clear other selections
                        updateField('race', checked ? ['all'] : []);
                      } else {
                        // If any specific race is selected, remove "All" and toggle the race
                        let newRace = formData.race.filter(r => r !== 'all');
                        if (checked) {
                          newRace = [...newRace, option.value];
                        } else {
                          newRace = newRace.filter(r => r !== option.value);
                        }
                        // If no races selected, default to "All"
                        if (newRace.length === 0) {
                          newRace = ['all'];
                        }
                        updateField('race', newRace);
                      }
                    }}
                  />
                  <Label htmlFor={option.value} className="text-sm font-normal cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau American Community Survey data (some categories use estimated data)
            </p>
          </div>

          {/* Marital Status */}
          <div className="space-y-2">
            <Label>Marital Status Preference</Label>
            <Select value={formData.maritalStatus} onValueChange={(value) => updateField('maritalStatus', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="never-married">Never Married</SelectItem>
                <SelectItem value="divorced-separated">Divorced/Separated</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau Marital Status data
            </p>
          </div>

          {/* Political Leaning */}
          <div className="space-y-2">
            <Label>Political Leaning Preference</Label>
            <Select value={formData.politics} onValueChange={(value) => updateField('politics', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="conservative">Conservative</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="liberal">Liberal</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on Pew Research Center Political Typology data
            </p>
          </div>

          {/* Feminist Identification */}
          <div className="space-y-2">
            <Label>Feminist Identification</Label>
            <Select value={formData.feminist} onValueChange={(value) => updateField('feminist', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="no">No (Does not identify as feminist)</SelectItem>
                <SelectItem value="yes">Yes (Identifies as feminist)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on YouGov Feminist Identification Survey data
            </p>
          </div>

          {/* Children */}
          <div className="space-y-2">
            <Label>Parental Status</Label>
            <Select value={formData.children} onValueChange={(value) => updateField('children', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="no-children">No Children</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau American Community Survey data
            </p>
          </div>

          {/* Body Type */}
          <div className="space-y-2">
            <Label>Body Type Preference</Label>
            <Select value={formData.bodyType} onValueChange={(value) => updateField('bodyType', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="not-overweight">Not Overweight/Obese (BMI &lt; 25)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on CDC National Health and Nutrition Examination Survey data
            </p>
          </div>

          {/* Family Structure */}
          <div className="space-y-2">
            <Label>Family Structure Background</Label>
            <Select value={formData.familyStructure} onValueChange={(value) => updateField('familyStructure', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="two-parents">Raised by Two Parents</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on General Social Survey data
            </p>
          </div>

          {/* Sexual Partner Count */}
          <div className="space-y-2">
            <Label>Sexual Experience Level</Label>
            <Select value={formData.sexualPartnerCount} onValueChange={(value) => updateField('sexualPartnerCount', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="zero">Virgin/Pure (0 partners)</SelectItem>
                <SelectItem value="low">Low Experience (1-3 partners)</SelectItem>
                <SelectItem value="moderate">Moderate Experience (4-9 partners)</SelectItem>
                <SelectItem value="high">High Experience (10+ partners)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on CDC National Survey of Family Growth data
            </p>
          </div>

          {/* Educational Level */}
          <div className="space-y-2">
            <Label>Educational Level Preference</Label>
            <Select value={formData.educationalLevel} onValueChange={(value) => updateField('educationalLevel', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="high_school_or_less">High School or Less</SelectItem>
                <SelectItem value="some_college">Some College</SelectItem>
                <SelectItem value="bachelors_degree">Bachelor's Degree</SelectItem>
                <SelectItem value="graduate_degree">Graduate Degree</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau Educational Attainment data
            </p>
          </div>

          <Button type="submit" className="w-full" size="lg">
            Calculate Compatibility Probability
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}